/**
 * Pronto · PublicListingV2 — iter v28 r61 (rewrite)
 *
 * Direzione utente (vocale + screenshot anteprima):
 *   «Strutturerei la pagina pubblica come l'anteprima del tab Annuncio:
 *    foto grande in cima con cover che cambia, descrizione a sx +
 *    galleria a dx, specs con simbolini, logo agenzia + nome in alto,
 *    pill VENDITA a destra. I 4 stili (Elegante, Luxury, Moderno, Minimal)
 *    cambiano SOLO font, colori e disposizione, NON il layout base.»
 *
 * Struttura unica condivisa dai 4 stili:
 *   1. HERO: cover slideshow + logo+nome agenzia top-left + pill VENDITA top-right
 *   2. HEADER: titolo + prezzo + city
 *   3. SPECS strip: mq, locali, bagni, piano, classe energetica (con icone)
 *   4. BODY: descrizione sx (3/5) + galleria 2x2 dx (2/5)
 *   5. MAP: indirizzo + iframe OSM con pin esatto
 *   6. CONTACT: card agente
 *   7. FOOTER: "Generata con Pronto" centrato (sparisce su piano Top)
 *
 * Le 4 varianti applicano data-style="elegante|luxury|moderno|minimal" al
 * wrapper. Tutto il theming è in CSS (.pl-v2[data-style="..."] selectors).
 */
import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import {
  MapPin, House, Bathtub, Lightning, Stack, Bed, Calendar, ArrowSquareOut,
  CalendarBlank, Envelope, Phone, X, ArrowsLeftRight, WhatsappLogo,
  ThermometerSimple, Diamond, Buildings, Compass, Armchair, CurrencyEur,
  FileText, Star, ShareNetwork, Check,
} from "@phosphor-icons/react";
import { publicFileUrl } from "@/lib/publicApi";
import { MapContainer, TileLayer, Marker, Tooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { COLORS as VOLANTINO_COLORS } from "@/components/StaticTemplatePreview";
import { readableTextOn } from "@/components/staticTemplate/tokens";

const fmtPrice = (p) => p ? `€ ${Number(p).toLocaleString("it-IT")}` : null;

// iter v28 r62 — Helper: hex (#RRGGBB) → rgba con alpha custom (per gradiente banda hero)
function hexToRgba(hex, alpha = 1) {
  const c = String(hex || "").replace("#", "");
  if (c.length !== 6) return `rgba(124,139,110,${alpha})`;
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// iter v28 r225 — User M1145: mix con nero per ottenere una versione molto
// scura di una palette chiara. Usato per Lussuoso quando l'utente sceglie
// un brand color chiaro (Bianco, Bianco Pastello, ecc.) — l'estetica dark
// elegante richiede sempre uno sfondo profondo.
function mixWithBlack(hex, darkness = 0.8) {
  const c = String(hex || "").replace("#", "");
  if (c.length !== 6) return "#1C1B1A";
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  const f = (v) => Math.max(0, Math.min(255, Math.round(v * (1 - darkness))));
  return `#${f(r).toString(16).padStart(2, "0")}${f(g).toString(16).padStart(2, "0")}${f(b).toString(16).padStart(2, "0")}`;
}

export default function PublicListingV2({ data, style, onBook }) {
  const p = data?.p || data?.property || {};
  const photos = data?.photos || [];
  const agency = data?.agency || {};
  const publicId = data?.publicId || data?.listing?.public_id;
  // iter v28 r126 — User M1044: pagina online deve avere gli STESSI 4 layout
  // dei social (Moderno · Elegante · Minimal · Lussuoso) con font coerenti.
  // STYLE_MAP semplificato: ogni nome è anche `data-style`, niente più `_v2`.
  const _styleRaw = style || data?.listing?.style || "moderno";
  const STYLE_MAP = {
    "moderno": "moderno",
    "elegante": "elegante",
    "minimal": "minimal",
    "lussuoso": "lussuoso",
    // legacy alias
    "elegante_old": "moderno",
    "luxury": "elegante",
    "": "moderno",
  };
  const effectiveStyle = STYLE_MAP[_styleRaw] || "moderno";
  const brandColor = agency?.brand_color || "#7C8B6E";

  // iter v28 r137 — User M1053/M1054: «provo notte → dovrebbe diventare TUTTO
  // scuro, descrizioni bianche, tutte le parti scure».
  // Approccio: il `brand_color` salvato è l'accent oro (#C8B886 per Notte,
  // #FAFAF7 per Bianco) NON lo sfondo. Match retro su VOLANTINO_COLORS
  // tramite hex accent → recupero il `bg` e `isDark` della palette così
  // posso forzare data-theme="dark" + bg + accent corretti.
  const _palette = (() => {
    const hex = (brandColor || "").toLowerCase();
    for (const c of Object.values(VOLANTINO_COLORS)) {
      if ((c.accent || "").toLowerCase() === hex) {
        return c;
      }
    }
    return null;
  })();
  const _isDarkBrand = !!(_palette && _palette.isDark);
  // iter v28 r233 — User M1149: stile Lussuoso DEVE restare bianco anche
  // con palette scure (Notte / Nero). L'utente apre la pagina su desktop
  // e vedeva due "barre nere" laterali fuori dalla card → orribile.
  // Lussuoso ora ignora completamente la cascade dark; solo la barra
  // caratteristiche (.pl-specs) si tinge col brand color.
  const _effectiveStyleForTheme = effectiveStyle;
  const dataTheme = (_isDarkBrand && _effectiveStyleForTheme !== "lussuoso") ? "dark" : undefined;
  const themeBg = (_isDarkBrand && _palette?.bg && _effectiveStyleForTheme !== "lussuoso") ? _palette.bg : null;

  // iter v28 r188 — User M1108: «stili pagina pubblica online siano coerenti
  // con colore scelto». Con i nuovi colori scuri (bordeaux/bosco/cioccolato)
  // il bg hero diventava corretto ma le card interne restavano `#1A222E`
  // (era hardcoded "Notte"). Derivo `--pl-card-dark` dal bg della palette:
  // mix col nero al 18% per ottenere una tonalità leggermente più scura del
  // bg principale, così le card si distinguono ma restano coerenti.
  const _darkenHex = (hex, ratio = 0.18) => {
    if (!hex || !hex.startsWith("#") || hex.length !== 7) return hex;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const f = (v) => Math.max(0, Math.min(255, Math.round(v * (1 - ratio))));
    return `#${f(r).toString(16).padStart(2, "0")}${f(g).toString(16).padStart(2, "0")}${f(b).toString(16).padStart(2, "0")}`;
  };
  const themeCardBg = (_isDarkBrand && themeBg && _effectiveStyleForTheme !== "lussuoso") ? _darkenHex(themeBg, 0.18) : null;
  // Card "contact" più calda (mix più chiaro) per dare profondità a tre livelli:
  // bg principale → card → contact card (un po' più chiara).
  const _lightenHex = (hex, ratio = 0.10) => {
    if (!hex || !hex.startsWith("#") || hex.length !== 7) return hex;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const f = (v) => Math.max(0, Math.min(255, Math.round(v + (255 - v) * ratio)));
    return `#${f(r).toString(16).padStart(2, "0")}${f(g).toString(16).padStart(2, "0")}${f(b).toString(16).padStart(2, "0")}`;
  };
  const themeContactBg = _isDarkBrand && themeBg ? _lightenHex(themeBg, 0.08) : null;

  // iter v28 r62 — Filtra le foto per categoria:
  //   • heroSlides: solo foto vere (no planimetria, no before/after pair)
  //   • beforeAfterPairs: foto con `staged_url` + `original_url` (l'API
  //     incapsula la coppia dentro lo stesso item, non come 2 foto separate)
  //   • galleryThumbs: tutte le foto regolari (no pair se mode=slider)
  const isPlan = (ph) => ph.is_plan || ph.is_floorplan || (ph.kind || "photo") === "floor_plan" || ph.photo_kind === "floor_plan";
  // iter v28 r86 — Coppia prima/dopo: il backend espone `staged_url` +
  // `original_url` quando la foto è stata trasformata (arredata o cambiata).
  // Prima cercavamo `role==="before"/"after"` su foto separate → match
  // sempre VUOTO → nessuno slider veniva mai mostrato.
  const isPair = (ph) => !!(ph.staged_url && ph.original_url && !isPlan(ph));

  // iter v28 r69 — Modalità Prima/Dopo decisa dall'agente nella tab
  // Condivisione. Default "slider".
  //   • "slider"      → galleria esclude pair; sezione slider sotto
  //   • "only_before" → pair mostra original_url nella galleria; no slider
  //   • "only_after"  → pair mostra staged_url nella galleria; no slider
  const baMode = (p?.before_after_mode || "slider").toLowerCase();
  const showBASection = p?.show_before_after_section !== false;

  // Costruisci le coppie before/after dalle foto con staged_url+original_url
  const beforeAfterPairs = photos
    .filter(isPair)
    .map((ph) => ({
      before: { ...ph, url: ph.original_url },
      after: { ...ph, url: ph.staged_url },
    }));

  // Foto "pure" eleggibili per hero (no plan)
  const heroEligible = photos.filter((ph) => !isPlan(ph));
  const heroSlides = heroEligible.slice(0, 6);

  const [slide, setSlide] = useState(0);
  useEffect(() => {
    if (heroSlides.length < 2) return;
    // iter v28 r76 — Auto-advance ogni 6s (era 5s) per dare più respiro
    // alla transizione 1.8s di crossfade. Risultato: cinematico, non a scatto.
    const t = setInterval(() => setSlide((s) => (s + 1) % heroSlides.length), 6000);
    return () => clearInterval(t);
  }, [heroSlides.length]);

  // iter v28 r89 — In modalità "slider" le pair sono incluse nella galleria
  // come MINI-SLIDER (non più come sezione separata sotto). Aggiungiamo un
  // flag `_isPair` per il rendering thumb. Le pair vengono spostate in coda
  // così sono accessibili dal "+N · Vedi tutte le foto".
  // iter v28 r91 — DEDUP: se una pair contiene staged_url/original_url,
  // rimuoviamo dalla galleria eventuali ALTRE foto statiche che usano gli
  // stessi path (così non vediamo "anche la foto statica del prima e del
  // dopo" — vedi richiesta utente). Una pair = UN solo entry nello slider.
  const pairPaths = new Set();
  if (baMode === "slider") {
    photos.forEach((ph) => {
      if (isPair(ph)) {
        if (ph.original_url) pairPaths.add(ph.original_url);
        if (ph.staged_url) pairPaths.add(ph.staged_url);
      }
    });
  }

  const galleryAll = [...photos]
    .filter((ph) => {
      // Foto pair: tienila sempre (sarà renderizzata come slider)
      if (isPair(ph)) return true;
      // Foto NON pair: scarta se la sua `url` coincide con un path già
      // coperto da una pair (= duplicato statico del prima/dopo)
      if (baMode === "slider" && pairPaths.has(ph.url)) return false;
      return true;
    })
    .map((ph) => {
      if (baMode === "slider" && isPair(ph)) {
        return { ...ph, _isPair: true };
      }
      if (baMode === "only_before" && isPair(ph)) {
        return { ...ph, url: ph.original_url };
      }
      return ph;
    })
    .sort((a, b) => {
      // iter v28 r90 — Pair in CODA in modalità slider (richiesta utente:
      // "come ultime, devi sempre metterle come ultime"). Le pair restano
      // accessibili dalla "+N · Vedi tutte le foto" → lightbox con slider.
      if (baMode === "slider") {
        return (a._isPair ? 1 : 0) - (b._isPair ? 1 : 0);
      }
      return 0;
    });
  // iter v28 r86 — Galleria: MAX 4 thumb visibili (2x2 grid). L'ultima
  // riceve overlay "+N foto · Vedi tutte" se ci sono extra. Su DESKTOP
  // l'altezza di ogni thumb viene calcolata per riempire l'altezza della
  // descrizione → grandi tanto quanto il testo, mai oltre.
  const MAX_VISIBLE = 4;
  const descRef = useRef(null);
  const galleryGridRef = useRef(null);
  const [thumbHeight, setThumbHeight] = useState(null); // px su desktop, null su mobile

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    let raf = 0;
    const measure = () => {
      const mq = window.matchMedia("(min-width: 640px)");
      if (!mq.matches) { setThumbHeight(null); return; }
      const descEl = descRef.current;
      if (!descEl) return;
      // iter v28 r86 — Misuriamo il CONTENUTO testo (scrollHeight del div
      // interno), NON l'offsetHeight del wrapper grid: il wrapper si stira
      // alla riga più alta del grid → loop di feedback infinito quando il
      // thumb si ridimensiona a fill di descH.
      const textInner = descEl.querySelector('[data-pl-desc-inner]');
      const eyebrow = descEl.querySelector('[data-pl-desc-eyebrow]');
      const targetH = (textInner?.scrollHeight || 0) + (eyebrow?.offsetHeight || 0) + 12;
      if (!targetH || targetH < 100) return;
      const gap = 8;
      const visibleCount = Math.min(MAX_VISIBLE, galleryAll.length);
      const rows = Math.max(1, Math.ceil(visibleCount / 2));
      const raw = Math.floor((targetH - (rows - 1) * gap) / rows);
      const h = Math.min(700, Math.max(200, raw)); // hard caps
      setThumbHeight((prev) => (prev === h ? prev : h));
    };
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };
    schedule();
    // iter v28 r86 — Osserviamo SOLO il contenuto testo (non il wrapper grid)
    // così evitiamo loop di feedback con la galleria che cresce.
    const textInner = descRef.current?.querySelector('[data-pl-desc-inner]');
    const ro = new ResizeObserver(schedule);
    if (textInner) ro.observe(textInner);
    window.addEventListener("resize", schedule);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", schedule);
      cancelAnimationFrame(raf);
    };
  }, [galleryAll.length, p?.ai_description, p?.description]);

  const visibleCount = Math.min(MAX_VISIBLE, galleryAll.length);
  const galleryThumbsDesktop = galleryAll.slice(0, visibleCount);
  // Mobile carousel: facciamo vedere fino a 6 thumb scrollabili (come prima)
  const mobileThumbCount = Math.min(6, galleryAll.length);
  const galleryThumbsMobile = galleryAll.slice(0, mobileThumbCount);
  const remainingCountDesktop = Math.max(0, galleryAll.length - visibleCount);
  const remainingCountMobile = Math.max(0, galleryAll.length - mobileThumbCount);
  const isAffitto = p?.listing_type === "affitto";

  // iter v28 r63 — Lightbox lifted up: state nel parent, passato a thumb+component
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const openLightbox = (i) => setLightboxIdx(i);
  const closeLightbox = () => setLightboxIdx(null);

  // Frase indirizzo breve per banda hero
  const heroAddress = [p?.address, p?.city].filter(Boolean).join(" · ");

  return (
    <div className="pl-v2 min-h-screen" data-style={effectiveStyle} data-theme={dataTheme} data-testid="pl-v2-root" style={{ "--pl-brand": brandColor, "--pl-gold": _palette?.accent || "#C8B886", "--pl-bg-dark": themeBg || "#0F1620", "--pl-card-dark": themeCardBg || "#1A222E", "--pl-contact-dark": themeContactBg || "#232C3A" }}>
      <article className="pl-card mx-auto bg-white overflow-hidden">

        {/* ═══════ HERO ═══════ */}
        {/* iter v28 r71 — Hero diverso per stile (cambio layout, non solo CSS).
            Luxury → cornice editoriale stile rivista (cream + foto framed).
            Minimal → split 60/40 verticale (text panel sx + foto dx).
            Elegante & Moderno → default (foto full-bleed + band colorata bottom). */}
        {/* iter v28 r128 — User M1046: Elegante torna al fallback hero (banda
            gradient bottom come post social Elegante). HeroLuxury usato solo per
            legacy `luxury` (mantenuto per back-compat). */}
        {effectiveStyle === "minimal" ? (
          <HeroMinimal
            heroSlides={heroSlides}
            slide={slide}
            setSlide={setSlide}
            agency={agency}
            p={p}
            brandColor={brandColor}
            publicId={publicId}
            isAffitto={isAffitto}
            heroAddress={heroAddress}
            fmtPrice={fmtPrice}
          />
        ) : effectiveStyle === "lussuoso" ? (
          <HeroModerno
            heroSlides={heroSlides}
            slide={slide}
            setSlide={setSlide}
            agency={agency}
            p={p}
            brandColor={brandColor}
            publicId={publicId}
            isAffitto={isAffitto}
            heroAddress={heroAddress}
            fmtPrice={fmtPrice}
          />
        ) : (
          <section className="pl-hero relative aspect-[16/10] bg-[#F1EEE6] overflow-hidden" data-testid="pl-hero">
          {/* Slideshow — iter v29 r247 thumb 1280px (max display size hero mobile) */}
          {heroSlides.map((ph, i) => (
            <img
              key={ph.id || i}
              src={publicFileUrl(publicId, ph.url, 1280)}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              style={{ opacity: i === slide ? 1 : 0, transition: "opacity 1800ms cubic-bezier(0.22, 1, 0.36, 1)" }}
              draggable={false}
            />
          ))}
          {/* Overlay gradient bottom-readable
              iter v28 r214 — User M1135: rinforzato overlay scuro inferiore
              su mobile per garantire leggibilità del titolo + prezzo (era
              poco visibile su sfondi chiari come letti bianchi).
              iter v28 r230 — User M1146: gradient più "stretto" verso il basso
              (parte al 70% invece che al 50%) così la foto resta protagonista
              su mobile e l'oscuramento non invade metà immagine. */}
          <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.18) 0%, transparent 18%, transparent 70%, rgba(0,0,0,0.50) 92%, rgba(0,0,0,0.65) 100%)" }} />

          {/* iter v28 r130 — Logo agency spostato TOP-LEFT per liberare top-right
              alla share-bar QR/WhatsApp/Condividi (più visibili).
              iter v28 r214+r215 — User M1135+M1136:
              • Loghi colorati → wrappa in pillbox bianco semi-trasparente
              • Loghi monocromatici → filtro auto: nero se brand chiaro (banda
                bianca), bianco se brand scuro (banda nera/notte/Notte). */}
          <div className="absolute top-3 sm:top-5 left-3 sm:left-5 z-10 max-w-[42%]" data-testid="pl-hero-brand">
            {agency?.logo_url ? (
              <img
                src={agency.logo_url}
                alt={agency.name || ""}
                className="h-7 sm:h-9 lg:h-11 w-auto max-w-[100px] sm:max-w-[150px] lg:max-w-[170px] object-contain block"
                style={
                  readableTextOn(brandColor) === "#1C1C1A"
                    ? { filter: "brightness(0) drop-shadow(0 1px 4px rgba(255,255,255,0.6))" }
                    : { filter: "brightness(0) invert(1) drop-shadow(0 1px 4px rgba(0,0,0,0.35))" }
                }
              />
            ) : agency?.name && (
              <div className="pl-brand-name text-[12px] sm:text-base" style={{ color: readableTextOn(brandColor), textShadow: "0 1px 8px rgba(0,0,0,0.5)" }}>
                {agency.name}
              </div>
            )}
          </div>

          {/* iter v28 r128 — Banda hero stile post social con PREZZO a destra
              (user M1046: «nel moderno il prezzo mettilo sulla barra sfumata
              a destra in bianco»).
              iter v28 r215 — User M1136: inversione automatica testi+logo
              in base alla brightness del brand_color. Se palette Bianco
              (banda chiara) → testi neri; se Nero/Notte → testi bianchi.
              iter v28 r225 — User M1145: su MOBILE il prezzo va SOTTO al
              titolo (non più side-by-side flex) per evitare titoli che
              spezzano in 4 righe e prezzi sovrapposti. */}
          <div
            className="absolute bottom-0 left-0 right-0 z-10 px-3 sm:px-8 lg:px-10 pt-6 sm:pt-20 lg:pt-24 pb-2 sm:pb-2.5"
            style={{ background: `linear-gradient(to top, ${hexToRgba(brandColor, 0.94)} 0%, ${hexToRgba(brandColor, 0.82)} 55%, ${hexToRgba(brandColor, 0.40)} 85%, ${hexToRgba(brandColor, 0)} 100%)`, color: readableTextOn(brandColor) }}
            data-testid="pl-hero-band"
          >
            {/* iter v28 r233 — User M1149: su mobile prezzo allineato a
                destra dell'indirizzo (stessa riga). Su desktop resta il
                vecchio layout (titolo+indirizzo a sx, prezzo a dx full). */}
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-1 sm:gap-4">
              <div className="flex-1 min-w-0">
                <div className="text-[8.5px] sm:text-[11px] uppercase tracking-[0.22em] sm:tracking-[0.28em] font-bold opacity-80 mb-0.5 sm:mb-2" data-testid="pl-hero-status">
                  {isAffitto ? "In affitto" : "In vendita"}
                </div>
                <h1 className="pl-title leading-tight" data-testid="pl-hero-title" style={{ color: readableTextOn(brandColor), wordBreak: "break-word", hyphens: "auto" }}>
                  {p?.title || "Senza titolo"}
                </h1>
                {/* MOBILE only: indirizzo + prezzo sulla stessa riga */}
                <div className="sm:hidden mt-1 flex items-center justify-between gap-2">
                  {heroAddress ? (
                    <div className="text-[9.5px] flex items-center gap-1 opacity-95 min-w-0 flex-1 truncate">
                      <MapPin size={10} weight="fill" className="flex-shrink-0" /> <span className="truncate">{heroAddress}</span>
                    </div>
                  ) : <div className="flex-1" />}
                  <div className="pl-price pl-price-mobile tabular-nums whitespace-nowrap flex-shrink-0" data-testid="pl-price-mobile" style={{ color: readableTextOn(brandColor) }}>
                    {p?.price_hidden ? <em className="italic font-medium opacity-90">Trattativa riservata</em> : (fmtPrice(p?.price) || <em className="italic font-medium opacity-90">Trattativa riservata</em>)}
                    {isAffitto && !p?.price_hidden && p?.price && <span className="pl-price-suffix opacity-90"> /mese</span>}
                  </div>
                </div>
                {/* DESKTOP only: indirizzo full sotto al titolo */}
                {heroAddress && (
                  <div className="hidden sm:flex mt-2.5 text-[13px] items-center gap-1.5 opacity-95">
                    <MapPin size={11} weight="fill" /> {heroAddress}
                  </div>
                )}
              </div>
              {/* DESKTOP only: prezzo full a destra */}
              <div className="hidden sm:flex pl-price tabular-nums whitespace-nowrap flex-shrink-0 sm:self-end sm:pb-0.5" data-testid="pl-price" style={{ color: readableTextOn(brandColor) }}>
                {p?.price_hidden ? <em className="italic font-medium opacity-90">Trattativa riservata</em> : (fmtPrice(p?.price) || <em className="italic font-medium opacity-90">Trattativa riservata</em>)}
                {isAffitto && !p?.price_hidden && p?.price && <span className="pl-price-suffix opacity-90"> /mese</span>}
              </div>
            </div>
          </div>

          {/* iter v28 r68 — Share button bottom-right del hero: bianco, discreto,
              cliccabile da chiunque per condividere il link su WhatsApp / SMS /
              email / app native (navigator.share). Fallback: copia in clipboard. */}
          <ShareFloatingButton publicId={publicId} title={p?.title} />

          {/* iter v28 r70 — Slideshow dots: spostati top-CENTER (libero da
              share button top-right e da logo top-left). */}
          {heroSlides.length > 1 && (
            <div className="absolute top-4 sm:top-7 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5">
              {heroSlides.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setSlide(i)}
                  className="rounded-full transition-all flex-shrink-0 p-0 m-0 border-0 appearance-none"
                  style={{
                    width: 7, height: 7,
                    minWidth: 7, minHeight: 7,
                    maxWidth: 7, maxHeight: 7,
                    boxSizing: "border-box",
                    lineHeight: 0,
                    background: i === slide ? "#FFFFFF" : "rgba(255,255,255,0.5)",
                  }}
                  aria-label={`Foto ${i + 1}`}
                />
              ))}
            </div>
          )}
          </section>
        )}

        {/* ═══════ HEADER (prezzo) — Nascosto per tutti i 4 stili V2
            (prezzo già nella banda hero / pricebox dedicato) ═══════ */}
        {effectiveStyle !== "elegante" && effectiveStyle !== "minimal" && effectiveStyle !== "lussuoso" && effectiveStyle !== "moderno" && (
          <header className="pl-header px-5 sm:px-10 pt-5 pb-3" data-testid="pl-header">
            <div className="flex items-baseline justify-between gap-4 flex-wrap">
              <div className="pl-eyebrow" style={{ color: brandColor }}>
                {isAffitto ? "Canone mensile" : "Prezzo"}
              </div>
              <span className="pl-price tabular-nums" data-testid="pl-price">
                {p?.price_hidden ? <em className="italic">Trattativa riservata</em> : (fmtPrice(p?.price) || <em className="italic">Trattativa riservata</em>)}
                {isAffitto && !p?.price_hidden && p?.price && <span className="pl-price-suffix"> /mese</span>}
              </span>
            </div>
          </header>
        )}

        {/* iter v28 r65 — Riepilogo caratteristiche RIMESSO (era stato eliminato in r63).
            Strip discreta sotto al prezzo con tutte le specs (mq, locali, bagni,
            piano, classe, anno) — utile come "scheda tecnica veloce" prima
            della galleria. */}
        <div className="pl-specs px-5 sm:px-10 py-3 sm:py-4 border-y" data-testid="pl-specs">
          <div className="flex items-center gap-x-5 gap-y-2 flex-wrap text-[11.5px] sm:text-[12.5px]">
            {p?.surface && (
              <span className="inline-flex items-center gap-1.5" data-testid="pl-spec-surface">
                <Stack size={13} weight="bold" className="pl-spec-icon" /> <strong>{p.surface}</strong> m²
              </span>
            )}
            {p?.rooms && (
              <span className="inline-flex items-center gap-1.5" data-testid="pl-spec-rooms">
                <House size={13} weight="bold" className="pl-spec-icon" /> <strong>{p.rooms}</strong> {p.rooms === 1 ? "locale" : "locali"}
              </span>
            )}
            {p?.bathrooms && (
              <span className="inline-flex items-center gap-1.5" data-testid="pl-spec-bathrooms">
                <Bathtub size={13} weight="bold" className="pl-spec-icon" /> <strong>{p.bathrooms}</strong> {p.bathrooms === 1 ? "bagno" : "bagni"}
              </span>
            )}
            {p?.floor && (
              <span className="inline-flex items-center gap-1.5" data-testid="pl-spec-floor">
                <Bed size={13} weight="bold" className="pl-spec-icon" /> Piano <strong>{p.floor}</strong>
              </span>
            )}
            {p?.energy_class && (
              <span className="inline-flex items-center gap-1.5" data-testid="pl-spec-energy">
                <Lightning size={13} weight="bold" className="pl-spec-icon" /> Classe <strong>{p.energy_class}</strong>
              </span>
            )}
            {p?.year_built && (
              <span className="inline-flex items-center gap-1.5" data-testid="pl-spec-year">
                <Calendar size={13} weight="bold" className="pl-spec-icon" /> Anno <strong>{p.year_built}</strong>
              </span>
            )}
            {p?.heating && (
              <span className="inline-flex items-center gap-1.5" data-testid="pl-spec-heating">
                <ThermometerSimple size={13} weight="bold" className="pl-spec-icon" /> {p.heating}
              </span>
            )}
            {p?.condition && (
              <span className="inline-flex items-center gap-1.5" data-testid="pl-spec-condition">
                <Diamond size={13} weight="bold" className="pl-spec-icon" /> {p.condition}
              </span>
            )}
          </div>
        </div>

        {/* iter v28 r68 — Layout: Descrizione a sinistra, Galleria a destra (richiesta utente). */}
        <div className="px-5 sm:px-10 py-6 sm:py-8 grid grid-cols-1 lg:grid-cols-5 gap-6 sm:gap-8" data-testid="pl-body">
          {/* Descrizione 2/5 — sinistra */}
          <div ref={descRef} className="lg:col-span-2 lg:order-1" data-testid="pl-description">
            <div className="pl-eyebrow mb-3" data-pl-desc-eyebrow>Descrizione</div>
            <div className="pl-body-text whitespace-pre-line" data-pl-desc-inner>
              {p?.ai_description || p?.description || <em style={{ opacity: 0.5 }}>Nessuna descrizione disponibile.</em>}
            </div>
          </div>
          {/* Galleria 3/5 — destra */}
          <div className="lg:col-span-3 lg:order-2" data-testid="pl-gallery">
            <div className="pl-eyebrow mb-3">Galleria · {galleryAll.length} foto</div>
            {galleryAll.length > 0 ? (
              <>
                {/* iter v28 r82 — Mobile: carosello orizzontale snap.
                    Desktop ≥sm: griglia 2 colonne, altezza ALLINEATA al testo
                    (calcolata via useLayoutEffect su descRef).
                    L'ULTIMA thumb visibile riceve overlay "+N foto · vedi tutte"
                    quando ci sono foto extra non mostrate. */}

                {/* MOBILE — carousel orizzontale (sm:hidden) */}
                <div className="sm:hidden flex gap-2 overflow-x-auto snap-x snap-mandatory -mx-5 px-5 pb-1" data-testid="pl-gallery-row-mobile">
                  {galleryThumbsMobile.map((ph, i) => {
                    const plan = isPlan(ph);
                    const isLast = i === galleryThumbsMobile.length - 1;
                    const hasOverlay = isLast && remainingCountMobile > 0;
                    // iter v28 r89 — Thumb mini-slider per le coppie Prima/Dopo
                    if (ph._isPair && !hasOverlay) {
                      return (
                        <div
                          key={ph.id || i}
                          className="flex-shrink-0 w-[78%] aspect-square overflow-hidden bg-[#F1EEE6] group relative snap-start"
                          data-testid={`pl-gallery-thumb-m-${i}`}
                        >
                          <BeforeAfterSlider
                            before={{ ...ph, url: ph.original_url }}
                            after={{ ...ph, url: ph.staged_url }}
                            publicId={publicId}
                            brandColor={brandColor}
                            idx={`m-${i}`}
                            compact
                          />
                        </div>
                      );
                    }
                    return (
                      <button
                        key={ph.id || i}
                        onClick={() => openLightbox(galleryAll.indexOf(ph))}
                        className="flex-shrink-0 w-[78%] aspect-square overflow-hidden bg-[#F1EEE6] group relative cursor-pointer snap-start"
                        data-testid={`pl-gallery-thumb-m-${i}`}
                      >
                        <img
                          src={publicFileUrl(publicId, ph.url, 640)}
                          alt=""
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          style={plan ? { background: "#F4F4F0", padding: 4, objectFit: "contain" } : undefined}
                          draggable={false}
                          loading="lazy"
                        />
                        {plan && !hasOverlay && (
                          <span className="absolute bottom-1.5 left-1.5 px-2 py-0.5 text-[9px] uppercase tracking-[0.16em] font-bold" style={{ background: "rgba(255,255,255,0.95)", color: "#1C1C1A" }}>
                            Pianta
                          </span>
                        )}
                        {hasOverlay && (
                          <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-3" style={{ background: "rgba(28,27,26,0.62)", backdropFilter: "blur(2px)" }}>
                            <div className="text-2xl font-bold tabular-nums leading-none">+{remainingCountMobile}</div>
                            <div className="mt-1.5 text-[9px] uppercase tracking-[0.18em] font-semibold opacity-95">
                              Vedi tutte le foto
                            </div>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* DESKTOP — grid 2 col, altezza = altezza descrizione (max 4 thumb) */}
                <div ref={galleryGridRef} className="hidden sm:grid sm:grid-cols-2 gap-2" data-testid="pl-gallery-row">
                  {galleryThumbsDesktop.map((ph, i) => {
                    const plan = isPlan(ph);
                    const isLast = i === galleryThumbsDesktop.length - 1;
                    const hasOverlay = isLast && remainingCountDesktop > 0;
                    const thumbStyle = thumbHeight ? { height: `${thumbHeight}px` } : undefined;
                    // iter v28 r89 — Thumb mini-slider per le coppie Prima/Dopo
                    if (ph._isPair && !hasOverlay) {
                      return (
                        <div
                          key={ph.id || i}
                          className="overflow-hidden bg-[#F1EEE6] relative h-64"
                          style={thumbStyle}
                          data-pl-thumb={i === 0 ? "1" : undefined}
                          data-testid={`pl-gallery-thumb-${i}`}
                        >
                          <BeforeAfterSlider
                            before={{ ...ph, url: ph.original_url }}
                            after={{ ...ph, url: ph.staged_url }}
                            publicId={publicId}
                            brandColor={brandColor}
                            idx={i}
                            compact
                          />
                        </div>
                      );
                    }
                    return (
                      <button
                        key={ph.id || i}
                        onClick={() => openLightbox(galleryAll.indexOf(ph))}
                        className="overflow-hidden bg-[#F1EEE6] group relative cursor-pointer h-64"
                        style={thumbStyle}
                        data-pl-thumb={i === 0 ? "1" : undefined}
                        data-testid={`pl-gallery-thumb-${i}`}
                      >
                        <img
                          src={publicFileUrl(publicId, ph.url, 640)}
                          alt=""
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          style={plan ? { background: "#F4F4F0", padding: 4, objectFit: "contain" } : undefined}
                          draggable={false}
                          loading="lazy"
                        />
                        {plan && !hasOverlay && (
                          <span className="absolute bottom-1.5 left-1.5 px-2 py-0.5 text-[9px] uppercase tracking-[0.16em] font-bold" style={{ background: "rgba(255,255,255,0.95)", color: "#1C1C1A" }}>
                            Pianta
                          </span>
                        )}
                        {hasOverlay && (
                          <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-3" style={{ background: "rgba(28,27,26,0.62)", backdropFilter: "blur(2px)" }}>
                            <div className="text-2xl sm:text-3xl font-bold tabular-nums leading-none">+{remainingCountDesktop}</div>
                            <div className="mt-1.5 text-[9px] sm:text-[10px] uppercase tracking-[0.18em] font-semibold opacity-95">
                              Vedi tutte le foto
                            </div>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="text-[12px] italic" style={{ color: "#9A968B" }}>
                Carica più foto per popolare la galleria.
              </div>
            )}

            {/* iter v28 r89 — La sezione "Prima · Dopo" è stata SPOSTATA
                dentro la galleria: le coppie pair appaiono come mini-slider
                tra le thumb (richiesta utente: "il prima e dopo lo metterei
                dentro nella galleria e non sotto"). */}
          </div>
          {/* (Descrizione spostata in alto a sinistra — iter v28 r68) */}
        </div>

        {/* ═══════ CARATTERISTICHE (stile scheda tecnica immobiliare.it) ═══════ */}
        <CaratteristicheSection p={p} brandColor={brandColor} />

        {/* ═══════ MAP ═══════ */}
        <MapSection p={p} brandColor={brandColor} />

        {/* ═══════ CONTACT ═══════ */}
        <ContactSection agency={agency} brandColor={brandColor} onBook={onBook} />
      </article>

      {/* ═══════ FOOTER ═══════ */}
      <FooterAgency agency={agency} />

      {/* iter v28 r63 — Lightbox controllato dal parent (state proper) */}
      {lightboxIdx !== null && (
        <Lightbox photos={galleryAll} startIdx={lightboxIdx} publicId={publicId} brandColor={brandColor} onClose={closeLightbox} />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   HeroLuxury — Cornice editoriale stile rivista
   (cream + foto framed + titolo italic centrato + city
   con linee laterali + prezzo gold + specs centrate)
   iter v28 r71
   ───────────────────────────────────────────────────────── */
function HeroLuxury({ heroSlides, slide, setSlide, agency, p, brandColor, publicId, isAffitto, fmtPrice }) {
  const cityLine = [p?.neighborhood, p?.city].filter(Boolean).map((s) => String(s).toUpperCase()).join(" · ");
  const descQuote = (p?.ai_description || p?.description || "").trim().split(/[.\n]/)[0]?.slice(0, 90);
  const showPrice = !p?.price_hidden && p?.price;
  return (
    <section className="pl-hero-luxury relative px-5 sm:px-10 lg:px-16 pt-8 sm:pt-12 lg:pt-16 pb-8 sm:pb-12" data-testid="pl-hero">
      <ShareFloatingButton publicId={publicId} title={p?.title} />

      {/* TOP — agency wordmark centrato
          iter v28 r219 — User M1139: logo SEMPRE nero su sfondo carta chiaro
          (Elegante usa #F4F1EA) — filter brightness(0). */}
      <div className="text-center max-w-3xl mx-auto" data-testid="pl-hero-brand">
        {agency?.logo_url ? (
          <img
            src={agency.logo_url}
            alt={agency.name || ""}
            className="h-9 sm:h-11 lg:h-14 w-auto max-w-[200px] mx-auto object-contain"
            style={{ filter: "brightness(0)" }}
          />
        ) : agency?.name ? (
          <div className="pl-brand-name">{agency.name}</div>
        ) : null}
        <div className="mt-2.5 sm:mt-3 text-[10px] sm:text-[11px] tracking-[0.36em] uppercase opacity-70 font-medium" style={{ fontFamily: "'Manrope',sans-serif" }}>
          Real estate
        </div>
        <div className="mx-auto mt-5 sm:mt-7 w-14 h-px" style={{ background: brandColor, opacity: 0.5 }} />
      </div>

      {/* iter v28 r76 — Titolo italic nero sopra la foto, SENZA banda
          sfumata (richiesta utente: "il titolo nero sopra senza la banda"). */}
      <div className="px-5 sm:px-10 lg:px-16 text-center max-w-3xl mx-auto mt-5 sm:mt-7 mb-5 sm:mb-7">
        <h1 className="pl-title leading-tight" data-testid="pl-hero-title">
          {p?.title || "Senza titolo"}
        </h1>
      </div>

      {/* Photo framed + slideshow */}
      <div className="relative w-full max-w-3xl mx-auto" data-testid="pl-hero-frame">
        {/* iter v28 r82 — Aspect più landscape su desktop (era 3/4 portrait
            che tagliava le foto immobiliari orizzontali). 4/3 mantiene la
            cornice editoriale ma evita il crop. */}
        <div className="relative aspect-[4/5] sm:aspect-[4/3] overflow-hidden bg-[#F1EEE6]" style={{ border: `1px solid ${brandColor}55` }}>
          {heroSlides.map((ph, i) => (
            <img
              key={ph.id || i}
              src={publicFileUrl(publicId, ph.url, 1280)}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              style={{ opacity: i === slide ? 1 : 0, transition: "opacity 1800ms cubic-bezier(0.22, 1, 0.36, 1)" }}
              draggable={false}
            />
          ))}
        </div>
        {heroSlides.length > 1 && (
          <div className="mt-3 flex items-center gap-1.5 justify-center">
            {heroSlides.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setSlide(i)}
                className="rounded-full transition-all flex-shrink-0 p-0 m-0 border-0 appearance-none"
                style={{
                  width: 7, height: 7, minWidth: 7, minHeight: 7, maxWidth: 7, maxHeight: 7,
                  boxSizing: "border-box", lineHeight: 0,
                  background: i === slide ? brandColor : `${brandColor}55`,
                }}
                aria-label={`Foto ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Quote descrizione */}
      {descQuote && (
        <div className="text-center max-w-2xl mx-auto mt-7 sm:mt-9 text-[10.5px] sm:text-[12px] tracking-[0.22em] uppercase opacity-75 leading-relaxed font-medium" style={{ fontFamily: "'Manrope',sans-serif" }}>
          «&nbsp;{descQuote}&nbsp;…&nbsp;»
        </div>
      )}

      {/* City line con righe laterali */}
      {cityLine && (
        <div className="flex items-center gap-3 sm:gap-5 justify-center max-w-md mx-auto mt-6 sm:mt-8" data-testid="pl-hero-city">
          <div className="h-px flex-1 max-w-[100px]" style={{ background: brandColor, opacity: 0.4 }} />
          <div className="text-[11px] sm:text-[12px] tracking-[0.32em] uppercase font-semibold whitespace-nowrap" style={{ fontFamily: "'Manrope',sans-serif" }}>
            {cityLine}
          </div>
          <div className="h-px flex-1 max-w-[100px]" style={{ background: brandColor, opacity: 0.4 }} />
        </div>
      )}

      {/* Prezzo big + specs */}
      <div className="text-center mt-5 sm:mt-7" data-testid="pl-hero-pricebox">
        <div className="pl-price tabular-nums" data-testid="pl-price">
          {p?.price_hidden ? <em className="italic">Trattativa riservata</em> : (fmtPrice(p?.price) || <em className="italic">Trattativa riservata</em>)}
          {isAffitto && showPrice && <span className="pl-price-suffix"> /mese</span>}
        </div>
        {(p?.surface || p?.rooms) && (
          <div className="mt-2 sm:mt-3 text-[10.5px] sm:text-[12px] tracking-[0.28em] uppercase opacity-75 font-semibold" style={{ fontFamily: "'Manrope',sans-serif" }}>
            {[p?.surface && `${p.surface} MQ`, p?.rooms && `${p.rooms} ${p.rooms === 1 ? "LOC." : "LOC."}`].filter(Boolean).join(" · ")}
          </div>
        )}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────
   HeroMinimal — Split 60/40 verticale
   (text panel sinistra + foto destra a tutta altezza)
   iter v28 r71
   ───────────────────────────────────────────────────────── */
function HeroMinimal({ heroSlides, slide, setSlide, agency, p, brandColor, publicId, isAffitto, fmtPrice }) {
  const ltype = isAffitto ? "Affitto" : "Vendita";
  return (
    <section className="pl-hero-minimal" data-testid="pl-hero">
      {/* iter v28 r77 — Top bar full-width con logo agenzia + eyebrow.
          iter v28 r80 — Ridotto pb così logo+foto+contenuti sono più vicini.
          iter v28 r81 — Eyebrow "APPARTAMENTO · VENDITA" allineato a destra
          in linea con il logo (richiesta utente). */}
      <div className="px-6 sm:px-8 lg:px-10 pt-6 sm:pt-8 pb-3 sm:pb-4 bg-white flex items-center justify-between gap-3" data-testid="pl-hero-topbar">
        <div data-testid="pl-hero-brand" className="flex-shrink-0">
          {agency?.logo_url ? (
            <img
              src={agency.logo_url}
              alt={agency.name || ""}
              className="h-8 sm:h-10 lg:h-12 w-auto max-w-[140px] object-contain"
              style={{ filter: "brightness(0)" }}
            />
          ) : agency?.name && (
            <div className="pl-brand-name">{agency.name}</div>
          )}
        </div>
        <div
          className="text-[9px] sm:text-[10px] tracking-[0.28em] uppercase font-light text-right whitespace-nowrap"
          style={{ color: brandColor }}
          data-testid="pl-hero-status"
        >
          {p?.property_type || "Immobile"} · {ltype}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 min-h-[70vh] lg:min-h-[560px]">
      {/* Right photo panel (mobile: sopra, dal lg in poi: dx) */}
      <div className="relative lg:col-span-3 lg:order-2 order-1 aspect-[4/3] lg:aspect-auto bg-[#F1EEE6] overflow-hidden">
        {heroSlides.map((ph, i) => (
          <img
            key={ph.id || i}
            src={publicFileUrl(publicId, ph.url, 1280)}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            style={{ opacity: i === slide ? 1 : 0, transition: "opacity 1800ms cubic-bezier(0.22, 1, 0.36, 1)" }}
            draggable={false}
          />
        ))}
        {/* iter v28 r81 — Eyebrow spostato nella top bar accanto al logo. */}
        <ShareFloatingButton publicId={publicId} title={p?.title} />
        {heroSlides.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5">
            {heroSlides.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setSlide(i)}
                className="rounded-full transition-all flex-shrink-0 p-0 m-0 border-0 appearance-none"
                style={{
                  width: 7, height: 7, minWidth: 7, minHeight: 7, maxWidth: 7, maxHeight: 7,
                  boxSizing: "border-box", lineHeight: 0,
                  background: i === slide ? "#FFFFFF" : "rgba(255,255,255,0.5)",
                }}
                aria-label={`Foto ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Left text panel — senza logo (è in top bar) */}
      <div className="lg:col-span-2 lg:order-1 order-2 flex flex-col px-6 sm:px-8 lg:px-10 pt-4 sm:pt-5 lg:pt-6 pb-6 sm:pb-8 lg:pb-10 relative" data-testid="pl-hero-textpanel">

        {/* Mid: titolo + descrizione
            iter v28 r78/r80 — Margin top minimo per portare l'eyebrow/titolo
            il più vicino possibile alla foto (richiesta utente).
            iter v28 r81 — Eyebrow spostato sulla foto in alto a destra. */}
        <div className="mt-1 sm:mt-2 lg:mt-0 lg:mb-auto">
          <h1 className="pl-title leading-[1.05]" data-testid="pl-hero-title">
            {p?.title || "Senza titolo"}
          </h1>
          {(p?.ai_description || p?.description) && (
            <p className="mt-5 sm:mt-6 text-[12.5px] sm:text-[13.5px] leading-relaxed font-light" style={{ color: "#52525B" }}>
              {String(p?.ai_description || p?.description).trim().split(/[.\n]/)[0]?.slice(0, 140)}…
            </p>
          )}
          {/* iter v28 r80 — Specs sotto descrizione RIMOSSE (richiesta utente):
              mq/locali ora visibili solo nella strip caratteristiche sotto.
              Evita la duplicazione. */}
        </div>

        {/* Bottom: prezzo
            iter v28 r80 — "IN VENDITA" allineato baseline al prezzo (era item-end
            che faceva risultare l'eyebrow molto in basso). */}
        <div className="mt-6 lg:mt-8 pt-4 lg:pt-5 border-t" style={{ borderColor: `${brandColor}40` }} data-testid="pl-hero-pricebox">
          <div className="flex items-baseline justify-between gap-4">
            <div className="text-[9.5px] sm:text-[10.5px] tracking-[0.28em] uppercase font-light leading-tight opacity-75">
              {isAffitto ? "Canone mensile" : "In vendita"}
            </div>
            <div className="pl-price tabular-nums text-right" data-testid="pl-price">
              {p?.price_hidden ? <em className="italic">Trattativa riservata</em> : (fmtPrice(p?.price) || <em className="italic">Trattativa riservata</em>)}
              {isAffitto && !p?.price_hidden && p?.price && <span className="pl-price-suffix"> /mese</span>}
            </div>
          </div>
        </div>
      </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────
   HeroModerno — Block design editoriale
   (foto top + banda SOLIDA full-width sotto con titolo in
   stampatello grassetto + nome agenzia + prezzo block)
   iter v28 r73
   ───────────────────────────────────────────────────────── */
/* ─────────────────────────────────────────────────────────
   HeroModerno — usato per stile LUSSUOSO.
   iter v28 r127 — BG dark + Bodoni Moda REGULAR (no italic, ≠ Elegante)
   + accent ORO #B59A63. Foto framed con bordo gold.
   ───────────────────────────────────────────────────────── */
function HeroModerno({ heroSlides, slide, setSlide, agency, p, brandColor, publicId, isAffitto, heroAddress, fmtPrice }) {
  const GOLD = "#B59A63";
  const cityLine = [p?.neighborhood, p?.city].filter(Boolean).map((s) => String(s).toUpperCase()).join(" · ");
  const showPrice = !p?.price_hidden && p?.price;
  // iter v28 r233 — User M1149: Lussuoso ora SEMPRE su sfondo bianco
  // (era dark con brand mixWithBlack). Le due "barre nere" laterali fuori
  // dalla card su desktop ora spariscono. Il brand color viene applicato
  // solo a .pl-specs (vedi CSS sotto). Logo agenzia → NERO.
  const stageBg = "#FFFFFF";
  const stageFg = "#1C1B1A";
  return (
    <section className="pl-hero-moderno relative px-5 sm:px-10 lg:px-16 pt-8 sm:pt-12 lg:pt-16 pb-8 sm:pb-12" style={{ background: stageBg, color: stageFg }} data-testid="pl-hero">
      <ShareFloatingButton publicId={publicId} title={p?.title} />

      {/* Logo agenzia SEMPRE nero su sfondo bianco (iter v28 r233) */}
      <div className="text-center max-w-3xl mx-auto" data-testid="pl-hero-brand">
        {agency?.logo_url ? (
          <img
            src={agency.logo_url}
            alt={agency.name || ""}
            className="h-9 sm:h-11 lg:h-14 w-auto max-w-[200px] mx-auto object-contain"
            style={{ filter: "brightness(0)" }}
          />
        ) : agency?.name ? (
          <div className="pl-brand-name" style={{ color: "#1C1B1A" }}>{agency.name}</div>
        ) : null}
        <div className="mt-2.5 sm:mt-3 text-[10px] sm:text-[11px] tracking-[0.36em] uppercase font-medium" style={{ fontFamily: "'Space Grotesk', sans-serif", color: GOLD }}>
          — {isAffitto ? "In affitto" : "In vendita"} —
        </div>
        <div className="mx-auto mt-5 sm:mt-7 w-14 h-px" style={{ background: GOLD, opacity: 0.7 }} />
      </div>

      {/* Titolo nero su bianco */}
      <div className="px-5 sm:px-10 lg:px-16 text-center max-w-3xl mx-auto mt-5 sm:mt-7 mb-5 sm:mb-7">
        <h1 className="pl-title leading-tight" data-testid="pl-hero-title" style={{ color: "#1C1B1A" }}>
          {p?.title || "Senza titolo"}
        </h1>
      </div>

      {/* Photo framed con bordo gold */}
      <div className="relative w-full max-w-3xl mx-auto" data-testid="pl-hero-frame">
        <div className="relative aspect-[4/5] sm:aspect-[4/3] overflow-hidden" style={{ background: "#F4F1EA", border: `1px solid ${GOLD}` }}>
          {heroSlides.map((ph, i) => (
            <img
              key={ph.id || i}
              src={publicFileUrl(publicId, ph.url, 1280)}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              style={{ opacity: i === slide ? 1 : 0, transition: "opacity 1800ms cubic-bezier(0.22, 1, 0.36, 1)" }}
              draggable={false}
            />
          ))}
        </div>
        {heroSlides.length > 1 && (
          <div className="mt-3 flex items-center gap-1.5 justify-center">
            {heroSlides.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setSlide(i)}
                className="rounded-full transition-all flex-shrink-0 p-0 m-0 border-0 appearance-none"
                style={{
                  width: 7, height: 7, minWidth: 7, minHeight: 7, maxWidth: 7, maxHeight: 7,
                  boxSizing: "border-box", lineHeight: 0,
                  background: i === slide ? GOLD : `${GOLD}55`,
                }}
                aria-label={`Foto ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* City line con righe gold */}
      {cityLine && (
        <div className="flex items-center gap-3 sm:gap-5 justify-center max-w-md mx-auto mt-6 sm:mt-8" data-testid="pl-hero-city">
          <div className="h-px flex-1 max-w-[100px]" style={{ background: GOLD, opacity: 0.45 }} />
          <div className="text-[11px] sm:text-[12px] tracking-[0.32em] uppercase font-medium whitespace-nowrap" style={{ fontFamily: "'Space Grotesk', sans-serif", color: GOLD }}>
            {cityLine}
          </div>
          <div className="h-px flex-1 max-w-[100px]" style={{ background: GOLD, opacity: 0.45 }} />
        </div>
      )}

      {/* Prezzo GOLD + specs */}
      <div className="text-center mt-5 sm:mt-7" data-testid="pl-hero-pricebox">
        <div className="pl-price tabular-nums" data-testid="pl-price" style={{ color: GOLD }}>
          {p?.price_hidden ? <em className="italic">Trattativa riservata</em> : (fmtPrice(p?.price) || <em className="italic">Trattativa riservata</em>)}
          {isAffitto && showPrice && <span className="pl-price-suffix"> /mese</span>}
        </div>
        {(p?.surface || p?.rooms) && (
          <div className="mt-2 sm:mt-3 text-[10.5px] sm:text-[12px] tracking-[0.28em] uppercase font-medium" style={{ fontFamily: "'Space Grotesk', sans-serif", color: GOLD, opacity: 0.85 }}>
            {[p?.surface && `${p.surface} MQ`, p?.rooms && `${p.rooms} LOC.`].filter(Boolean).join(" · ")}
          </div>
        )}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────
   ShareFloatingButton — bottone bianco nel hero band per
   condividere il link via navigator.share (con fallback copy).
   Usa l'URL `/api/share/{public_id}` così i crawler social
   (WhatsApp/iMessage/FB) ricevono OG meta tags con foto +
   titolo + descrizione invece del logo Pronto generico.
   iter v28 r68
   ───────────────────────────────────────────────────────── */
function ShareFloatingButton({ publicId, title }) {
  const [copied, setCopied] = useState(false);
  if (!publicId) return null;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const shareUrl = `${origin}/api/share/${publicId}`;
  const shareText = title || "Annuncio immobiliare";

  const handleShare = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: shareText, text: shareText, url: shareUrl });
        return;
      } catch (_e) { /* user cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_e) { /* silent */ }
  };

  return (
    // iter v28 r134 — User M1051: «togli il qr e mostra solo icona condividi
    // non cosa puoi fare». Pulsante singolo icona-only, ben visibile (pillola
    // bianca con shadow), top-right.
    <button
      onClick={handleShare}
      className="absolute top-3 right-3 sm:top-5 sm:right-5 z-30 inline-flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/95 hover:bg-white active:scale-[0.96] transition-all shadow-lg"
      style={{ color: "#1C1B1A", backdropFilter: "blur(6px)" }}
      data-testid="pl-share-floating"
      aria-label="Condividi annuncio"
      title={copied ? "Link copiato" : "Condividi"}
    >
      {copied ? <Check size={20} weight="bold" className="text-[#7C8B6E]" /> : <ShareNetwork size={18} weight="bold" />}
    </button>
  );
}

/* ─────────────────────────────────────────────────────────
   Lightbox — controllato dal parent
   ───────────────────────────────────────────────────────── */
function Lightbox({ photos, startIdx, publicId, brandColor, onClose }) {
  const [idx, setIdx] = useState(startIdx);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + photos.length) % photos.length);
      else if (e.key === "ArrowRight") setIdx((i) => (i + 1) % photos.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [photos.length, onClose]);
  const ph = photos[idx] || photos[0];
  if (!ph) return null;
  // iter v28 r90 — Quando la foto corrente è una coppia Prima/Dopo,
  // mostra il BeforeAfterSlider full-size invece dell'immagine statica.
  const isPairItem = !!(ph._isPair && ph.staged_url && ph.original_url);
  return (
    <div className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center p-4" onClick={onClose} data-testid="pl-lightbox">
      <button onClick={(e) => { e.stopPropagation(); onClose(); }} className="absolute top-5 right-5 text-white/80 hover:text-white" data-testid="pl-lightbox-close">
        <X size={28} weight="bold" />
      </button>
      {isPairItem ? (
        <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
          <BeforeAfterSlider
            before={{ ...ph, url: ph.original_url }}
            after={{ ...ph, url: ph.staged_url }}
            publicId={publicId}
            brandColor={brandColor || "#7C8B6E"}
            idx={`lb-${idx}`}
          />
        </div>
      ) : (
        <img src={publicFileUrl(publicId, ph.url)} alt="" className="max-w-full max-h-[85vh] object-contain" onClick={(e) => e.stopPropagation()} />
      )}
      {photos.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); setIdx((i) => (i - 1 + photos.length) % photos.length); }} className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 text-white text-xl">←</button>
          <button onClick={(e) => { e.stopPropagation(); setIdx((i) => (i + 1) % photos.length); }} className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 text-white text-xl">→</button>
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 px-3 py-1 text-white text-xs sm:text-sm rounded-full bg-white/10">{idx + 1} / {photos.length}</div>
        </>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   QuickContactRow — 4 pulsanti sotto al prezzo (iter v28 r64)
   ───────────────────────────────────────────────────────── */
function QuickContactRow({ agency, brandColor, onBook }) {
  const agentEmail = agency?.agent_email || agency?.email;
  const agentPhone = agency?.agent_phone || agency?.phone;
  const phoneRaw = (agentPhone || "").replace(/\s+/g, "");
  const waNumber = phoneRaw.replace(/^\+/, "").replace(/^00/, "");
  const items = [];
  if (waNumber) items.push({
    href: `https://wa.me/${waNumber}?text=${encodeURIComponent("Salve, mi interessa l'immobile pubblicato online.")}`,
    label: "WhatsApp", icon: <WhatsappLogo size={14} weight="fill" />,
    color: "#25D366", isExternal: true, testid: "pl-quick-whatsapp",
  });
  if (agentPhone) items.push({
    href: `tel:${phoneRaw}`,
    label: "Chiama", icon: <Phone size={13} weight="fill" />,
    color: brandColor, testid: "pl-quick-phone",
  });
  if (agentEmail) items.push({
    href: `mailto:${agentEmail}?subject=${encodeURIComponent("Interesse immobile")}`,
    label: "Email", icon: <Envelope size={13} weight="fill" />,
    color: "#3F3D38", testid: "pl-quick-email",
  });
  items.push({
    onClick: onBook, label: "Prenota visita",
    icon: <CalendarBlank size={13} weight="fill" />,
    color: brandColor, filled: true, testid: "pl-quick-book",
  });

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="pl-quick-contacts">
      {items.map((it, i) => {
        const baseCls = "inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 sm:py-2.5 rounded-full text-[11.5px] sm:text-[12.5px] font-semibold transition-transform hover:scale-[0.97] active:scale-95";
        const style = it.filled
          ? { background: it.color, color: "#FFFFFF" }
          : { background: "#FFFFFF", color: it.color, border: `1.5px solid ${it.color}` };
        if (it.onClick) {
          return <button key={i} onClick={it.onClick} className={baseCls} style={style} data-testid={it.testid}>{it.icon} {it.label}</button>;
        }
        return <a key={i} href={it.href} {...(it.isExternal ? { target: "_blank", rel: "noreferrer" } : {})} className={baseCls} style={style} data-testid={it.testid}>{it.icon} {it.label}</a>;
      })}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   MapSection — Leaflet + Nominatim 2-step geocoding
   ───────────────────────────────────────────────────────── */
function MapSection({ p, brandColor }) {
  const cityName = (p?.city || "").trim();
  const streetQuery = (p?.address || "").trim();
  const query = [p?.address, p?.neighborhood, p?.city].filter(Boolean).join(", ");

  // iter v28 r65 — Coords PRIMARIE dal backend (geocoded server-side al save).
  // Fallback: geocoding client-side (per immobili creati prima di r65 che
  // non hanno ancora geo_lat/geo_lon in DB).
  const [coords, setCoords] = useState(
    (p?.geo_lat != null && p?.geo_lon != null) ? [p.geo_lat, p.geo_lon] : null
  );
  const [resolvedLabel, setResolvedLabel] = useState(p?.geo_label || "");

  useEffect(() => {
    // Se le coords sono GIÀ arrivate dal backend, niente fetch client.
    if (p?.geo_lat != null && p?.geo_lon != null) {
      setCoords([p.geo_lat, p.geo_lon]);
      setResolvedLabel(p.geo_label || query);
      return;
    }
    if (!query) return;
    let cancelled = false;
    const headers = { "Accept-Language": "it" };
    (async () => {
      try {
        let bbox = null, cityCenter = null;
        if (cityName) {
          const r1 = await fetch(`https://nominatim.openstreetmap.org/search?city=${encodeURIComponent(cityName)}&country=Italy&format=json&limit=1`, { headers });
          const d1 = await r1.json();
          if (d1?.[0]?.boundingbox) {
            const [s, n, w, e] = d1[0].boundingbox.map(parseFloat);
            bbox = `${w},${n},${e},${s}`;
            cityCenter = { lat: parseFloat(d1[0].lat), lon: parseFloat(d1[0].lon) };
          }
        }
        const tryQueries = [];
        if (streetQuery) {
          tryQueries.push(streetQuery);
          const noCivic = streetQuery.replace(/\s*[\d/]+(?:\s*[a-zA-Z])?\s*$/, "").trim();
          if (noCivic && noCivic !== streetQuery) tryQueries.push(noCivic);
        }
        let lat = null, lon = null, label = "";
        for (const q of tryQueries) {
          const url = bbox
            ? `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=1&viewbox=${bbox}&bounded=1&addressdetails=1`
            : `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q + (cityName ? ", " + cityName : ""))}&countrycodes=it&format=json&limit=1&addressdetails=1`;
          const rr = await fetch(url, { headers });
          const dd = await rr.json();
          if (cancelled) return;
          if (dd?.[0]) {
            lat = parseFloat(dd[0].lat);
            lon = parseFloat(dd[0].lon);
            label = dd[0].display_name?.split(",").slice(0, 3).join(",") || "";
            if (!isNaN(lat) && !isNaN(lon)) break;
          }
        }
        if ((lat === null || isNaN(lat)) && cityCenter) {
          lat = cityCenter.lat; lon = cityCenter.lon; label = cityName;
        }
        if (lat !== null && !isNaN(lat)) {
          setCoords([lat, lon]);
          setResolvedLabel(label);
        }
      } catch (_e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [query, cityName, streetQuery, p?.geo_lat, p?.geo_lon, p?.geo_label]);

  if (!query) return null;

  // iter v28 r62 — Custom pin SVG colorato col brand color
  const pinIcon = L.divIcon({
    className: "pl-map-pin",
    html: `<div style="width:30px;height:38px;display:flex;align-items:flex-start;justify-content:center;filter:drop-shadow(0 4px 8px rgba(0,0,0,0.35))">
      <svg width="30" height="38" viewBox="0 0 30 38" xmlns="http://www.w3.org/2000/svg">
        <path d="M15 0C6.7 0 0 6.7 0 15c0 11.25 15 23 15 23s15-11.75 15-23C30 6.7 23.3 0 15 0z" fill="${brandColor}"/>
        <circle cx="15" cy="14" r="5" fill="white"/>
      </svg>
    </div>`,
    iconSize: [30, 38],
    iconAnchor: [15, 38],
    popupAnchor: [0, -36],
  });

  // iter v28 r64 — Indirizzo nel titolo, città in eyebrow
  const titleStreet = (p?.address || "").trim();
  const titleNeighborhood = (p?.neighborhood || "").trim();
  // Cosa mostrare come "titolo" della sezione mappa:
  //  - se ho la via → "{Via}"
  //  - altrimenti → "{Quartiere}"
  //  - fallback → "Il quartiere"
  const sectionTitle = titleStreet || titleNeighborhood || "Il quartiere";

  return (
    <section className="px-5 sm:px-10 py-8 border-t" data-testid="pl-map-section">
      <div className="pl-eyebrow mb-2.5">{p?.city || "Dove si trova"}</div>
      <h2 className="pl-section-title mb-4">{sectionTitle}</h2>
      <div className="relative w-full overflow-hidden bg-[#F1EEE6]" style={{ aspectRatio: "16/9", borderRadius: 4 }}>
        {coords ? (
          <MapContainer
            center={coords}
            zoom={18}
            scrollWheelZoom={false}
            attributionControl={false}
            style={{ height: "100%", width: "100%" }}
            data-testid="pl-leaflet"
            key={`${coords[0]}-${coords[1]}`}
          >
            <TileLayer
              attribution=""
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker position={coords} icon={pinIcon}>
              <Tooltip permanent={true} direction="top" offset={[0, -34]} className="pl-map-tooltip">
                <strong style={{ fontSize: 11.5, color: "#1C1B1A" }}>{resolvedLabel || query}</strong>
              </Tooltip>
            </Marker>
          </MapContainer>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-[12px]" style={{ color: "#9A968B" }}>Caricamento mappa…</div>
        )}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────
   ContactSection — card agente con TUTTI i pulsanti
   ───────────────────────────────────────────────────────── */
function ContactSection({ agency, brandColor, onBook }) {
  const photoUrl = agency?.agent_photo_url || agency?.picture;
  const logoUrl = agency?.logo_url;
  const agentName = agency?.agent_name || agency?.name || "Agente immobiliare";
  const agencyName = agency?.name && agency?.name !== agency?.agent_name ? agency.name : null;
  const agentEmail = agency?.agent_email || agency?.email;
  const agentPhone = agency?.agent_phone || agency?.phone;
  const initials = agentName.split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";
  const phoneRaw = (agentPhone || "").replace(/\s+/g, "");
  const waNumber = phoneRaw.replace(/^\+/, "").replace(/^00/, "");

  // iter v32 r50 — User M1459: «perché non si vede foto?» Sulla PROD il
  // field `agent_photo_url` punta a un file mancante nell'object storage
  // → il browser mostra il placeholder "?" rotto invece del fallback.
  // Risolto con stateful onError: foto fallita → logo → iniziali.
  //
  // iter v34 r03 — User M1521: sull'app iOS il "?" tornava a comparire
  // sull'anteprima vetrina. Root cause: WKWebView iOS in certi scenari
  // (immagine che ritorna 200 con body malformato, o redirect cross-origin
  // che stalla) NON triggera `onError` → photoErrored resta false → l'utente
  // vede il broken-image nativo per sempre. Fix: safety-net con timeout 6s
  // che forza il fallback anche se `onError` non scatta (stesso pattern di
  // `AgentPhoto` in `AgentPublicProfile.jsx`).
  const [photoErrored, setPhotoErrored] = useState(false);
  const [photoLoaded, setPhotoLoaded] = useState(false);
  const [logoErrored, setLogoErrored] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const photoAvailable = photoUrl && !photoErrored;
  const logoAvailable = logoUrl && !logoErrored;

  // Safety-net: se dopo 6s l'immagine agente non ha triggerato né onLoad
  // né onError, la trattiamo come fallita e cadiamo sul livello successivo
  // (logo → iniziali). Evita che iOS WKWebView mostri il "?" placeholder
  // nativo indefinitamente. Stesso comportamento per il logo.
  useEffect(() => {
    if (!photoAvailable || photoLoaded) return undefined;
    const t = setTimeout(() => setPhotoErrored(true), 6000);
    return () => clearTimeout(t);
  }, [photoAvailable, photoLoaded, photoUrl]);
  useEffect(() => {
    if (photoAvailable || !logoAvailable || logoLoaded) return undefined;
    const t = setTimeout(() => setLogoErrored(true), 6000);
    return () => clearTimeout(t);
  }, [photoAvailable, logoAvailable, logoLoaded, logoUrl]);

  // iter v28 r135 — User M1052: priorità avatar UNIFORME su tutti i layout:
  //   1) Foto agente   → vince sempre (se caricabile davvero)
  //   2) Logo agenzia  → fallback se la foto manca o dà 404
  //   3) Iniziali      → fallback se mancano entrambi
  let AvatarContent;
  if (photoAvailable) {
    AvatarContent = (
      <img
        src={photoUrl}
        alt={agentName}
        className="w-24 h-24 rounded-full object-cover mb-4"
        style={{ boxShadow: `0 0 0 3px ${brandColor}, 0 8px 20px -8px ${hexToRgba(brandColor, 0.4)}` }}
        data-testid="pl-contact-agent-photo"
        onLoad={() => setPhotoLoaded(true)}
        onError={() => setPhotoErrored(true)}
      />
    );
  } else if (logoAvailable) {
    AvatarContent = (
      <div
        className="w-24 h-24 rounded-full flex items-center justify-center mb-4 overflow-hidden p-4 bg-white"
        style={{ border: `2px solid ${brandColor}`, boxShadow: `0 8px 20px -8px ${hexToRgba(brandColor, 0.25)}` }}
        data-testid="pl-contact-logo-avatar"
      >
        {/* iter v28 r233 — User M1149: logo agenzia in sezione Contatta
            SEMPRE nero (su tutti i 4 stili). L'utente preferisce uniformità
            visiva del logo rispetto al colore nativo del logo. */}
        <img src={logoUrl} alt={agencyName || ""} className="w-full h-full object-contain" style={{ filter: "brightness(0)" }} onLoad={() => setLogoLoaded(true)} onError={() => setLogoErrored(true)} />
      </div>
    );
  } else {
    AvatarContent = (
      <div
        className="w-24 h-24 rounded-full flex items-center justify-center text-3xl font-bold mb-4 bg-white"
        style={{ border: `2px solid ${brandColor}`, color: brandColor, boxShadow: `0 8px 20px -8px ${hexToRgba(brandColor, 0.25)}` }}
        data-testid="pl-contact-initials"
      >
        {initials}
      </div>
    );
  }

  return (
    <section className="pl-contact px-5 sm:px-10 py-10 border-t" data-testid="pl-contact-section">
      <div className="pl-eyebrow mb-2.5">Contatta</div>
      <h2 className="pl-section-title mb-7">Parliamo di questa casa</h2>
      <div className="pl-contact-card mx-auto max-w-xl bg-white rounded-2xl p-7 shadow-sm border" style={{ borderColor: "#EAE7DF" }}>
        <div className="flex flex-col items-center text-center">
          {AvatarContent}
          {/* iter v28 r135 — User M1052: nome AGENZIA piccolo SOPRA, nome
              AGENTE grande SOTTO. Stessa gerarchia su tutti i 4 layout. */}
          {agencyName && (
            <div className="text-[11px] uppercase tracking-[0.22em] mb-1" style={{ color: "#6B6A65" }} data-testid="pl-contact-agency-name">
              {agencyName}
            </div>
          )}
          <div className="pl-agent-name leading-tight" data-testid="pl-contact-agent-name">{agentName}</div>
          <div className="w-12 h-px my-5" style={{ background: brandColor, opacity: 0.5 }} />

          {/* iter v28 r62 — 4 pulsanti di contatto: WhatsApp, Telefono, Email, Prenota visita */}
          <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-3" data-testid="pl-contact-actions">
            {waNumber && (
              <a
                href={`https://wa.me/${waNumber}?text=${encodeURIComponent("Salve, mi interessa l'immobile pubblicato online.")}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-[13px] font-semibold transition-transform hover:scale-[0.98] bg-[#25D366] text-white"
                data-testid="pl-contact-whatsapp"
              >
                <WhatsappLogo size={15} weight="fill" /> WhatsApp
              </a>
            )}
            {agentPhone && (
              <a
                href={`tel:${phoneRaw}`}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-[13px] font-semibold border-2 transition-colors"
                style={{ borderColor: brandColor, color: brandColor, background: "white" }}
                data-testid="pl-contact-phone"
              >
                <Phone size={14} weight="fill" /> Chiama
              </a>
            )}
            {agentEmail && (
              <a
                href={`mailto:${agentEmail}?subject=${encodeURIComponent("Interesse immobile")}`}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-[13px] font-semibold border-2 transition-colors"
                style={{ borderColor: "#EAE7DF", color: "#3F3D38", background: "white" }}
                data-testid="pl-contact-email"
              >
                <Envelope size={14} weight="fill" /> Email
              </a>
            )}
            <button
              onClick={onBook}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-[13px] font-bold text-white transition-transform hover:scale-[0.98]"
              style={{ background: brandColor }}
              data-testid="pl-contact-cta"
            >
              <CalendarBlank size={14} weight="fill" /> Prenota visita
            </button>
          </div>

          {/* Info contatto in chiaro sotto */}
          {(agentEmail || agentPhone) && (
            <div className="mt-3 pt-3 border-t w-full text-[12px]" style={{ borderColor: "#EAE7DF", color: "#6B6A65" }}>
              {agentEmail && <div className="truncate"><Envelope size={11} className="inline mr-1.5" />{agentEmail}</div>}
              {agentPhone && <div className="mt-0.5"><Phone size={11} className="inline mr-1.5" />{agentPhone}</div>}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────
   BeforeAfterSlider — drag handle confronto due immagini
   iter v28 r89 — Riscritto con listener globali durante drag
   (movimento fluido anche quando il mouse esce dal box) e
   pointer-events API moderna. Funziona anche dentro la
   galleria come thumb compatta.
   ───────────────────────────────────────────────────────── */
function BeforeAfterSlider({ before, after, publicId, brandColor, idx, compact = false }) {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const draggingRef = useRef(false);

  // iter v28 r91 — Autoplay teaser quando lo slider entra nel viewport per
  // la prima volta. Una sola animazione (50 → 18 → 82 → 50) in ~3.8s che
  // suggerisce all'utente l'interazione possibile. Stop su user touch.
  // iter v28 r92 — Rallentato: step più ampi (1.0s/1.4s/1.4s) e transizione
  // CSS più lunga (0.9s ease-in-out) per evitare la sensazione "a scatto".
  const playedRef = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let timeouts = [];
    const playTeaser = () => {
      if (playedRef.current || draggingRef.current) return;
      playedRef.current = true;
      const steps = [
        { p: 18, t: 1000 },
        { p: 82, t: 2400 },
        { p: 50, t: 3800 },
      ];
      steps.forEach((s) => {
        timeouts.push(setTimeout(() => {
          if (!draggingRef.current) setPos(s.p);
        }, s.t));
      });
    };
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) playTeaser(); });
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => {
      obs.disconnect();
      timeouts.forEach(clearTimeout);
    };
  }, []);

  const setFromClientX = (clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    setPos(pct);
  };

  const onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    draggingRef.current = true;
    playedRef.current = true; // stoppa il teaser se ancora in corso
    try { ref.current?.setPointerCapture?.(e.pointerId); } catch { /* noop */ }
    setFromClientX(e.clientX);
    e.preventDefault();
  };
  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    setFromClientX(e.clientX);
  };
  const onPointerUp = (e) => {
    draggingRef.current = false;
    try { ref.current?.releasePointerCapture?.(e.pointerId); } catch { /* noop */ }
  };

  return (
    <div
      ref={ref}
      className="relative w-full h-full overflow-hidden bg-[#F1EEE6] select-none cursor-ew-resize touch-none"
      style={compact ? undefined : { aspectRatio: "4/3" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      data-testid={`pl-ba-slider-${idx}`}
    >
      <img src={publicFileUrl(publicId, after.url)} alt="Dopo" className="absolute inset-0 w-full h-full object-cover pointer-events-none" draggable={false} loading="lazy" />
      <div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)`, transition: draggingRef.current ? "none" : "clip-path 0.9s ease-in-out" }}
      >
        <img src={publicFileUrl(publicId, before.url)} alt="Prima" className="absolute inset-0 w-full h-full object-cover" draggable={false} loading="lazy" />
      </div>
      <span className={`absolute top-2 left-2 ${compact ? "px-1.5 py-0.5 text-[8px]" : "px-2 py-0.5 text-[9px]"} uppercase tracking-[0.18em] font-bold rounded-sm`} style={{ background: "rgba(255,255,255,0.95)", color: "#1C1C1A" }}>Prima</span>
      <span className={`absolute top-2 right-2 ${compact ? "px-1.5 py-0.5 text-[8px]" : "px-2 py-0.5 text-[9px]"} uppercase tracking-[0.18em] font-bold rounded-sm`} style={{ background: brandColor, color: "white" }}>Dopo</span>
      <div
        className="absolute top-0 bottom-0 pointer-events-none"
        style={{
          left: `${pos}%`,
          transform: "translateX(-50%)",
          width: 2,
          background: "white",
          boxShadow: "0 0 6px rgba(0,0,0,0.4)",
          transition: draggingRef.current ? "none" : "left 0.9s ease-in-out",
        }}
      >
        <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ${compact ? "w-7 h-7" : "w-9 h-9"} rounded-full bg-white flex items-center justify-center shadow-lg`} style={{ color: brandColor }}>
          <ArrowsLeftRight size={compact ? 13 : 16} weight="bold" />
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   CaratteristicheSection — scheda tecnica icone+label+value
   stile immobiliare.it (iter v28 r67)
   ───────────────────────────────────────────────────────── */
function CaratteristicheSection({ p, brandColor }) {
  const isAffitto = p?.listing_type === "affitto";
  const rows = [];

  if (p?.property_type) rows.push({ icon: <Buildings size={22} weight="regular" />, label: "Tipologia", value: p.property_type });
  rows.push({ icon: <FileText size={22} weight="regular" />, label: "Contratto", value: isAffitto ? "Affitto" : "Vendita" });
  if (p?.surface) rows.push({ icon: <Stack size={22} weight="regular" />, label: "Superficie", value: `${p.surface} m²` });
  if (p?.rooms) rows.push({ icon: <House size={22} weight="regular" />, label: "Locali", value: p.rooms });
  if (p?.bathrooms) rows.push({ icon: <Bathtub size={22} weight="regular" />, label: "Bagni", value: p.bathrooms });
  if (p?.floor) rows.push({ icon: <Bed size={22} weight="regular" />, label: "Piano", value: p.floor });
  if (p?.year_built) rows.push({ icon: <Calendar size={22} weight="regular" />, label: "Anno di costruzione", value: p.year_built });
  if (p?.year_renovated) rows.push({ icon: <CalendarBlank size={22} weight="regular" />, label: "Ristrutturazione", value: p.year_renovated });
  if (p?.energy_class) rows.push({ icon: <Lightning size={22} weight="regular" />, label: "Classe energetica", value: p.energy_class });
  if (p?.heating) rows.push({ icon: <ThermometerSimple size={22} weight="regular" />, label: "Riscaldamento", value: p.heating });
  if (p?.exposure) rows.push({ icon: <Compass size={22} weight="regular" />, label: "Esposizione", value: p.exposure });
  if (isAffitto && p?.furnished) rows.push({ icon: <Armchair size={22} weight="regular" />, label: "Arredamento", value: p.furnished });
  if (isAffitto && p?.monthly_fees != null) rows.push({ icon: <CurrencyEur size={22} weight="regular" />, label: "Spese mensili", value: `€ ${Number(p.monthly_fees).toLocaleString("it-IT")}` });
  if (isAffitto && p?.contract_type) rows.push({ icon: <FileText size={22} weight="regular" />, label: "Tipo contratto", value: p.contract_type });

  const features = Array.isArray(p?.features) ? p.features.filter(Boolean) : [];

  if (rows.length === 0 && features.length === 0) return null;

  return (
    <section className="pl-features px-5 sm:px-10 py-8 border-t" data-testid="pl-features-section">
      <div className="pl-eyebrow mb-2.5">Scheda tecnica</div>
      <h2 className="pl-section-title mb-5">Caratteristiche</h2>
      {rows.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
          {rows.map((r, i) => (
            <div key={i} className="flex items-start gap-3" data-testid={`pl-feature-${i}`}>
              {/* iter v28 r189 — User M1109: icone caratteristiche ORO + label
                  bianco su tema scuro. Uso classi `pl-feature-icon/label/value`
                  così il CSS dark-mode override (vedi index.css). */}
              <div className="pl-feature-icon flex-shrink-0 mt-0.5">
                {r.icon}
              </div>
              <div className="min-w-0">
                <div className="pl-feature-label text-[13px] sm:text-[14px] font-bold leading-tight">
                  {r.label}
                </div>
                <div className="pl-feature-value text-[12.5px] sm:text-[13.5px] mt-0.5 leading-snug">
                  {r.value}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {features.length > 0 && (
        <div className="pl-features-extra mt-7 pt-6 border-t">
          <div className="pl-eyebrow mb-3">
            Altre caratteristiche
          </div>
          <div className="flex flex-wrap gap-2">
            {features.map((f, i) => (
              <span
                key={i}
                className="pl-feature-tag inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px]"
                data-testid={`pl-feature-tag-${i}`}
              >
                <Star size={11} weight="fill" className="pl-feature-tag-icon" />
                {f}
              </span>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

/* ─────────────────────────────────────────────────────────
   FooterAgency — "Generata con Pronto"
   iter v28 r68 — "Pronto" usa il font del logo (Montserrat
   Medium uppercase, classe .font-brand). Sparisce solo per
   gli agenti in piano Top.
   ───────────────────────────────────────────────────────── */
function FooterAgency({ agency }) {
  const plan = (agency?.plan || "free").toLowerCase();
  if (plan === "pro") return null;
  return (
    <footer className="px-5 sm:px-10 py-8" style={{ background: "#FAFAF7", borderTop: "1px solid #EFEDE7" }} data-testid="pl-footer">
      <div className="max-w-5xl mx-auto flex items-center justify-center">
        <a href="https://pronto.casa" target="_blank" rel="noreferrer" className="inline-flex items-baseline gap-2" data-testid="pl-footer-pronto">
          <span className="text-[10.5px] uppercase tracking-[0.24em]" style={{ color: "#71717A", fontFamily: "'Manrope',sans-serif" }}>
            Generata con
          </span>
          <span className="font-brand" style={{ color: "#1C1B1A", fontSize: 14, lineHeight: 1 }}>
            Pronto
          </span>
        </a>
      </div>
    </footer>
  );
}
